/* CrochetSkein v13. Native horizontal scroll; no library or autoplay.
 * A fresh custom element initializes automatically when the theme editor reloads a section.
 * Disconnection cancels observers and listeners, including on editor section removal.
 */
if (!customElements.get('abd-product-carousel')) {
  customElements.define('abd-product-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-abd-track]');
      if (!this.track || this.controller) return;
      this.controller = new AbortController();
      this.previous = this.querySelector('[data-abd-prev]');
      this.next = this.querySelector('[data-abd-next]');
      this.controls = this.querySelector('[data-abd-controls]');
      this.currentLabel = this.querySelector('[data-abd-current]');
      this.totalLabel = this.querySelector('[data-abd-total]');
      this.targets = [0];
      const options = { signal: this.controller.signal };
      this.previous?.addEventListener('click', () => this.move(-1), options);
      this.next?.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        // Do not steal arrow keys from any form control or link inside a card.
        if (event.target !== this.track) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          this.move(rtl ? -direction : direction);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.goTo(event.key === 'Home' ? 0 : this.targets.length - 1);
        }
      }, options);
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(() => this.measure());
        this.observer.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.measure(), options);
      }
      this.classList.add('is-enhanced');
      this.measure();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    measure() {
      const first = this.track.firstElementChild;
      if (!first || !this.track.clientWidth) return;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap) || 0;
      const unit = first.getBoundingClientRect().width + gap;
      if (unit <= 0) return;
      const fullCards = Math.max(1, Math.floor((this.track.clientWidth + gap + 1) / unit));
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const pageWidth = unit * fullCards;
      this.targets = [0];
      // Last target is the real scroll boundary: the last card is never cut off.
      for (let pos = pageWidth; pos < max - 2; pos += pageWidth) this.targets.push(pos);
      if (max > 2) this.targets.push(max);
      if (this.totalLabel) this.totalLabel.textContent = String(this.targets.length);
      if (this.controls) this.controls.hidden = max <= 2;
      this.update();
    }

    update() {
      const position = Math.abs(this.track.scrollLeft);
      let nearest = 0;
      for (let i = 1; i < this.targets.length; i += 1) {
        if (Math.abs(this.targets[i] - position) < Math.abs(this.targets[nearest] - position)) nearest = i;
      }
      this.currentIndex = nearest;
      if (this.currentLabel) this.currentLabel.textContent = String(nearest + 1);
      if (this.previous) this.previous.disabled = position <= 2;
      if (this.next) this.next.disabled = position >= this.targets[this.targets.length - 1] - 2;
    }

    move(direction) {
      this.goTo(Math.max(0, Math.min(this.targets.length - 1, (this.currentIndex || 0) + direction)));
    }

    goTo(index) {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: this.targets[index] * (rtl ? -1 : 1), behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  });
}
